/**
 * Utility functions for date formatting and manipulation
 */

/**
 * Formats a date string for HTML date inputs (yyyy-MM-dd format)
 * Handles timezone issues by avoiding timezone conversion
 * 
 * @param dateString - The date string to format
 * @returns Formatted date string in yyyy-MM-dd format, or empty string if invalid
 */
export const formatDateForInput = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    // If the date string is already in YYYY-MM-DD format, return it as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // If the date string contains time information with 'T', extract just the date part
    if (dateString.includes('T')) {
      return dateString.split('T')[0];
    }
    
    // If the date string is in format "YYYY-MM-DD HH:mm:ss", extract just the date part
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString)) {
      return dateString.split(' ')[0];
    }
    
    // For other formats, parse the date and format it without timezone conversion
    const date = new Date(dateString + 'T00:00:00');
    if (isNaN(date.getTime())) {
      // console.warn('Invalid date format:', dateString);
      return '';
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (error) {
    // console.warn('Error formatting date for input:', dateString, error);
    return '';
  }
};

/**
 * Formats a date string for form inputs (yyyy-MM-dd format)
 * This is an alias for formatDateForInput for backward compatibility
 * 
 * @param dateString - The date string to format
 * @returns Formatted date string in yyyy-MM-dd format, or empty string if invalid
 */
export const formatDateForForm = (dateString: string): string => {
  return formatDateForInput(dateString);
};

/**
 * Formats a date string for API calls (yyyy-MM-dd format)
 * Extracts just the date part from ISO strings
 * 
 * @param dateString - The date string to format
 * @returns Formatted date string in yyyy-MM-dd format, or empty string if invalid
 */
export const formatDateForAPI = (dateString: string): string => {
  if (!dateString) return '';
  return dateString.split('T')[0];
};

/**
 * Formats a date for display purposes (e.g., "Jan 15, 2024")
 * 
 * @param dateString - The date string to format
 * @param options - Intl.DateTimeFormatOptions for customization
 * @returns Formatted date string for display
 */
export const formatDateForDisplay = (
  dateString: string, 
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  }
): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      // console.warn('Invalid date format for display:', dateString);
      return '';
    }
    
    return date.toLocaleDateString('en-US', options);
  } catch (error) {
    // console.warn('Error formatting date for display:', dateString, error);
    return '';
  }
};

/**
 * Gets the current date in yyyy-MM-dd format
 * 
 * @returns Current date in yyyy-MM-dd format
 */
export const getCurrentDate = (): string => {
  return new Date().toISOString().split('T')[0];
};

/**
 * Gets a future date in yyyy-MM-dd format
 * 
 * @param daysFromNow - Number of days from today
 * @returns Future date in yyyy-MM-dd format
 */
export const getFutureDate = (daysFromNow: number): string => {
  const futureDate = new Date();
  futureDate.setDate(futureDate.getDate() + daysFromNow);
  return futureDate.toISOString().split('T')[0];
};

/**
 * Validates if a date string is in yyyy-MM-dd format
 * 
 * @param dateString - The date string to validate
 * @returns True if the date string is in yyyy-MM-dd format
 */
export const isValidDateFormat = (dateString: string): boolean => {
  return /^\d{4}-\d{2}-\d{2}$/.test(dateString);
};

/**
 * Validates if a date string represents a valid date
 * 
 * @param dateString - The date string to validate
 * @returns True if the date string represents a valid date
 */
export const isValidDate = (dateString: string): boolean => {
  if (!dateString) return false;
  
  try {
    const date = new Date(dateString + 'T00:00:00');
    return !isNaN(date.getTime());
  } catch (error) {
    return false;
  }
};

/**
 * Formats a date for display purposes (e.g., "Jan 15, 2024")
 * This is an alias for formatDateForDisplay for backward compatibility
 * 
 * @param dateString - The date string to format
 * @param options - Intl.DateTimeFormatOptions for customization
 * @returns Formatted date string for display
 */
export const formatDate = (dateString: string, options?: Intl.DateTimeFormatOptions): string => {
  return formatDateForDisplay(dateString, options);
};

/**
 * Formats a date and time for display purposes (e.g., "Jan 15, 2024 at 2:30 PM")
 * 
 * @param date - The date to format (Date object or date string)
 * @param options - Intl.DateTimeFormatOptions for customization
 * @returns Formatted date and time string for display
 */
export const formatDateTime = (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
  if (!date) return '';
  
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) {
      // console.warn('Invalid date format for display:', date);
      return '';
    }
    
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      ...options
    };
    
    return dateObj.toLocaleDateString('en-US', defaultOptions);
  } catch (error) {
    // console.warn('Error formatting date and time for display:', date, error);
    return '';
  }
};

/**
 * Adds years to a date
 * 
 * @param date - The date to add years to
 * @param years - Number of years to add
 * @returns New date with years added
 */
export const addYears = (date: Date | string, years: number): Date => {
  const dateObj = date instanceof Date ? date : new Date(date);
  const newDate = new Date(dateObj);
  newDate.setFullYear(newDate.getFullYear() + years);
  return newDate;
};

/**
 * Adds years to a date and returns formatted string
 * 
 * @param date - The date to add years to
 * @param years - Number of years to add
 * @returns Formatted date string with years added
 */
export const addYearsToString = (date: Date | string, years: number): string => {
  const newDate = addYears(date, years);
  return formatDateTime(newDate);
};

/**
 * Adds days to a date
 * 
 * @param date - The date to add days to
 * @param days - Number of days to add
 * @returns New date with days added
 */
export const addDays = (date: Date | string, days: number): Date => {
  const dateObj = date instanceof Date ? date : new Date(date);
  const newDate = new Date(dateObj);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};

/**
 * Adds months to a date
 * 
 * @param date - The date to add months to
 * @param months - Number of months to add
 * @returns New date with months added
 */
export const addMonths = (date: Date | string, months: number): Date => {
  const dateObj = date instanceof Date ? date : new Date(date);
  const newDate = new Date(dateObj);
  newDate.setMonth(newDate.getMonth() + months);
  return newDate;
};

/**
 * Formats a date for API calls in "yyyy-MM-dd HH:mm:ss" format
 * 
 * @param date - The date to format (Date object or date string)
 * @returns Formatted date string in "yyyy-MM-dd HH:mm:ss" format
 */
export const formatDateTimeForAPI = (date: Date | string): string => {
  if (!date) return '';
  
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) {
      // console.warn('Invalid date format for API:', date);
      return '';
    }
    
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    const seconds = String(dateObj.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    // console.warn('Error formatting date for API:', date, error);
    return '';
  }
};

/**
 * Formats a date and time for datetime-local input fields (YYYY-MM-DDTHH:mm format)
 * 
 * @param date - The date to format (Date object or date string)
 * @returns Formatted date string in "YYYY-MM-DDTHH:mm" format for datetime-local input
 */
export const formatDateTimeForInput = (date: Date | string): string => {
  if (!date) return '';
  
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) {
      // console.warn('Invalid date format for datetime input:', date);
      return '';
    }
    
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch (error) {
    // console.warn('Error formatting date for datetime input:', date, error);
    return '';
  }
};

const INDIA_TIME_ZONE = 'Asia/Kolkata';
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

/** Parse signedOn from API (UTC ISO or naive IST wall-clock string). */
function parseSignedOnInstant(raw: string): Date | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(trimmed)) {
    const d = new Date(trimmed);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const naive = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/);
  if (naive) {
    const [, y, mo, d, h, mi, s = '0'] = naive;
    const istWallClockAsUtc = Date.UTC(+y, +mo - 1, +d, +h, +mi, +s);
    return new Date(istWallClockAsUtc - IST_OFFSET_MS);
  }

  const fallback = new Date(trimmed);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

/** E-invoice digital signature / meta: DD-MM-YYYY HH:mm:ss in IST */
export const formatDigitalSignedOnDisplay = (iso?: string | null): string => {
  if (!iso?.trim()) return '';
  const d = parseSignedOnInstant(iso);
  if (!d) return iso.trim();

  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: INDIA_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';

  return `${get('day')}-${get('month')}-${get('year')} ${get('hour')}:${get('minute')}:${get('second')}`;
};

/** Resolve e-invoice digital signature timestamp (invoice / credit-debit note headers). */
export function resolveSignedOnRaw(source?: Record<string, unknown> | null): string | undefined {
  if (!source) return undefined;
  const candidates = [
    source.signedOn,
    source.signed_on,
    source.signedAt,
    source.signed_at,
    source.digitalSignedOn,
    source.digital_signed_on,
  ];
  for (const raw of candidates) {
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
  }
  return undefined;
}

export function formatDigitalSignedOnLine(source?: Record<string, unknown> | null): string {
  return formatDigitalSignedOnDisplay(resolveSignedOnRaw(source));
}